/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { useFormik } from 'formik';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { FormControl, FormHelperText, InputLabel, OutlinedInput, Stack } from '@mui/material';

import { ZodTypes } from 'shared';

import { HUModal2 } from '../../../../components/HUModal2/HUModal2';
import { HUSpin } from '../../../../components/HUSpin/HUSpin';
import { notifyReportableApiError } from '../../../../components/notifyReportableApiError';
import { TeamApiService } from '../../../../services/TeamApiService';

type AddTeamFormValues = { identifier: string; displayName: string; about: string | null };

type Props = {
  onSuccess: () => void;
  onCancel: () => void;
  organizationId: string;
  existingTeamIdentifiers: string[];
  open: boolean;
};

export const AddTeamModal = memo<Props>(props => {
  const { t } = useTranslation();

  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const onSubmit = async (values: AddTeamFormValues) => {
    try {
      setIsProcessing(true);

      await TeamApiService.createTeam({
        organizationId: props.organizationId,
        data: {
          identifier: values.identifier.trim(),
          displayName: values.displayName.trim(),
          about: values.about?.trim() || null,
        },
      });

      setIsProcessing(false);
      props.onSuccess();
    } catch (error) {
      notifyReportableApiError(error).catch(error => {
        console.log('Failed to notify reportable api:', error);
      });
      setIsProcessing(false);
    }
  };

  const validationSchema = useMemo(
    () =>
      Yup.object({
        identifier: Yup.string()
          .trim()
          .required(t('organizationAdmin:teams.properties.identifier.errors.required'))
          .matches(ZodTypes.Team.IdentifierRegex, t('organizationAdmin:teams.properties.identifier.errors.regex'))
          .notOneOf(props.existingTeamIdentifiers, t('common:reportable_error.codes.duplicate_identifier')),
        displayName: Yup.string()
          .trim()
          .required(t('organizationAdmin:teams.properties.displayName.errors.required'))
          .matches(ZodTypes.Team.DisplayNameRegex, t('organizationAdmin:teams.properties.displayName.errors.regex')),
        about: Yup.string().trim().nullable(),
      }),
    [t, props.existingTeamIdentifiers],
  );

  const formik = useFormik<AddTeamFormValues>({
    enableReinitialize: true,
    initialValues: { identifier: '', displayName: '', about: '' },
    onSubmit,
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema,
  });

  const hasError = (field: keyof AddTeamFormValues): boolean => !!formik.touched[field] && !!formik.errors[field];

  const resetForm = formik.resetForm;
  useEffect(() => {
    if (!props.open) {
      resetForm();
    }
  }, [props.open, resetForm]);

  return (
    <HUModal2
      open={props.open}
      title={t('organizationAdmin:teams.add_team')}
      okButtonText={t('common:modal.submit')}
      okButtonProps={{ disabled: !formik.dirty }}
      cancelButtonText={t('common:modal.cancel')}
      cancelButtonProps={{ disabled: isProcessing }}
      onCancel={props.onCancel}
      disableRestoreFocus
      onOk={formik.submitForm}
      size="medium"
    >
      <HUSpin spinning={isProcessing} delay={50}>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={1}>
            <FormControl variant="outlined" error={hasError('identifier')}>
              <InputLabel htmlFor="identifier">{t('organizationAdmin:teams.properties.identifier.title')}</InputLabel>
              <OutlinedInput
                id="identifier"
                name="identifier"
                label={t('organizationAdmin:teams.properties.identifier.title')}
                fullWidth
                type="text"
                value={formik.values.identifier}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={hasError('identifier')}
                data-cy="add-team-identifier"
              />
              {hasError('identifier') && <FormHelperText>{formik.errors.identifier}</FormHelperText>}
            </FormControl>
            <FormControl variant="outlined" error={hasError('displayName')}>
              <InputLabel htmlFor="displayName">{t('organizationAdmin:teams.properties.displayName.title')}</InputLabel>
              <OutlinedInput
                id="displayName"
                name="displayName"
                label={t('organizationAdmin:teams.properties.displayName.title')}
                fullWidth
                type="text"
                value={formik.values.displayName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={hasError('displayName')}
                data-cy="add-team-display-name"
              />
              {hasError('displayName') && <FormHelperText>{formik.errors.displayName}</FormHelperText>}
            </FormControl>
            <FormControl variant="outlined" error={hasError('about')}>
              <InputLabel htmlFor="about">{t('organizationAdmin:teams.properties.about.title')}</InputLabel>
              <OutlinedInput
                id="about"
                name="about"
                label={t('organizationAdmin:teams.properties.about.title')}
                fullWidth
                type="text"
                value={formik.values.about}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={hasError('about')}
                data-cy="add-team-about"
              />
              {hasError('about') && <FormHelperText>{formik.errors.about}</FormHelperText>}
            </FormControl>
          </Stack>
        </form>
      </HUSpin>
    </HUModal2>
  );
});
